/**
 * Phase 1.13.2 — "Recommended" badge.
 *
 * Renders when isRecommended=true; null otherwise. The default (`'light'`)
 * tone is a gold-soft fill with sage-800 text and a gold dove glyph, so
 * the badge reads as tenant-affirmative across the directory surface.
 * Stale variant (older than STALE_AFTER_MS, ~6 months) degrades to a
 * neutral sand fill plus a clock glyph, signaling "verification
 * recommended" without alarming the read.
 *
 * `tone` picks the ink/fill pair: `'light'` (default) is the palette above, for
 * every use outside the band. `'onDark'` is the band's own meta line.
 * The fresh chip is a solid gold fill, `bg-warm-400`, with `text-forest-700`
 * for both the text and the dove glyph. The stale chip stays cream text
 * throughout on a `cream/10` fill. The fresh chip's opaque fill clears
 * 4.5:1 for text and 3:1 for the glyph regardless of what lies behind
 * it; the stale chip measures against the band's lightest gradient stop
 * (sage-800), with or without the dove watermark's 6% cream, the figures
 * are recorded in the PR. `text-label` (rem) and a rem `min-h` replace
 * the light variant's fixed 11-pixel text and 25-pixel height so the
 * badge stays in the type-scale allowlist and does not clip at 200%
 * zoom.
 *
 * Tooltip composes:
 *   - "Recommended"                                            (minimum)
 *   - "Recommended by {name} on {date}"                        (with metadata)
 *   - " — verification recommended"                            (when stale)
 *
 * NULL-tolerant on recommendedByName per the orphan-FK pattern:
 * server-side LEFT JOIN to users may resolve to null when the
 * recommender's row is is_active=false; the tooltip degrades to
 * date-only rather than printing the literal "null".
 *
 * Noted, not changed (contract addendum revision 4 §5 item 12): a
 * screen reader may not hear the stale state, since it lives only in
 * this span's `aria-label`/`title`, not in the visible "Recommended" text.
 */

import { type ReactNode } from 'react';
import { Clock } from 'lucide-react';
import { HubBrandMark } from '@/components/hub/HubBrandMark';
import { isRecommendationStale } from '@/lib/recommendation';

export interface RecommendedBadgeProps {
  isRecommended: boolean;
  recommendedAt: string | null;
  recommendedByName: string | null | undefined;
  /** Optional clock override (testing only). Defaults to Date.now(). */
  now?: number;
  /** `'light'` (default, unchanged) or `'onDark'` (the band's meta line). */
  tone?: 'light' | 'onDark';
}

export function RecommendedBadge({
  isRecommended,
  recommendedAt,
  recommendedByName,
  now,
  tone = 'light',
}: RecommendedBadgeProps): ReactNode {
  if (!isRecommended) return null;

  const stale = isRecommendationStale(recommendedAt, now);

  const dateText = recommendedAt
    ? new Date(recommendedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  const tooltipBase =
    recommendedByName && dateText
      ? `Recommended by ${recommendedByName} on ${dateText}`
      : dateText
        ? `Recommended on ${dateText}`
        : 'Recommended';
  const tooltip = stale ? `${tooltipBase} (verification recommended)` : tooltipBase;

  // X.6.6 refresh: gold tag + dove (brand-affirmative). Stale degrades to a
  // muted sand tag + a Lucide clock, signaling "verification recommended".
  // UX-13 S2: rounded-pill → rounded-tight per the geometry ruling (tags
  // take the tight radius; the full pill retires). Shared with the detail
  // header, so the restyle lands once here.
  //
  // onDark (the band's meta line) swaps to rem sizing (text-label + a rem
  // min-h) so it stays inside the type-scale allowlist and never clips at
  // 200% zoom. Fresh is a solid gold chip, bg-warm-400, with text-forest-700
  // for the text and the dove glyph. Stale stays cream text throughout on a
  // plain cream/10 fill (contrast figures in the PR).
  const onDark = tone === 'onDark';
  const className = onDark
    ? stale
      ? 'inline-flex min-h-[1.375rem] items-center gap-[7px] rounded-tight bg-cream/10 pl-[11px] pr-[13px] text-label font-medium text-cream'
      : 'inline-flex min-h-[1.375rem] items-center gap-[7px] rounded-tight bg-warm-400 pl-[11px] pr-[13px] text-label font-semibold text-forest-700'
    : stale
      ? 'inline-flex h-[25px] items-center gap-[7px] rounded-tight bg-sand pl-[11px] pr-[13px] text-[11px] font-medium text-ink-soft'
      : 'inline-flex h-[25px] items-center gap-[7px] rounded-tight bg-gold-soft pl-[11px] pr-[13px] text-[11px] font-semibold text-sage-800';

  return (
    <span
      className={className}
      title={tooltip}
      aria-label={tooltip}
      data-testid="recommended-badge"
      data-stale={stale ? 'true' : 'false'}
    >
      {stale ? (
        <Clock size={11} aria-hidden="true" />
      ) : (
        <HubBrandMark
          label=""
          className="flex w-[13px] [&>svg]:h-auto [&>svg]:w-full"
          style={{ color: onDark ? 'var(--color-forest-700)' : 'var(--color-warm-400)' }}
        />
      )}
      Recommended
    </span>
  );
}
